import { useEffect, useRef } from "react";

/* ═══════════════════════════════════════════════════════════
   Näkymän suodatin ↔ osoitteen kyselyosa.

   OSOITESÄÄNTÖ (main.tsx) sanoo: polku on identiteetti, kysely on
   tila. Tämä on sen täsmennys, ei poikkeus: suodatin joka MÄÄRITTELEE
   JOUKON jonka Projekti-Hubin luku nimeää on osa luvun identiteettiä.
   "48 avointa punchia kohteessa N-2410" ei ole tila jota kukaan
   säätää, se on väite — ja väitteen on oltava lähetettävissä
   linkkinä, ja F5:n on palautettava sama joukko.

   Rajaus: tähän menee vain se suodatin jolla joukko on määritelty
   (kohde, tila, tyyppi, vaikutus, ulottuvuus). Hakukenttä, järjestys
   ja avatut paneelit eivät mene — ne ovat yhä tilaa.

   Kaksi suuntaa, yksi muisti:
     osoite → näkymä   mount, takaisin-nappi, liitetty linkki
     näkymä → osoite   käyttäjä vaihtaa suodatinta

   `viimeksi` on se merkkijono jonka kumpi tahansa suunta viimeksi
   käsitteli. Ilman sitä oma kirjoitus laukaisisi oman lukemisen ja
   silmukka pyörisi; sama peruste kuin main.tsx:n hash-efektissä.

   ⚠ Näkymän on alustettava tilansa osoitteesta `useState`-
   alustajassa (`lueHaku`), ei vasta tässä. Muuten ensimmäinen
   kirjoitus lähtisi ALUSTAMATTOMASTA tilasta ja pyyhkisi linkin
   parametrit ennen kuin ne on luettu — ja hashchange toisi tyhjän
   takaisin näkymään. Tämä hook ohittaa siksi ensimmäisen kirjoituksen
   kokonaan: mountissa osoite on totuus, ei näkymä.
   ═══════════════════════════════════════════════════════════ */

type Arvo = string | number | boolean | null | undefined;

/** Suodatinarvot kyselymerkkijonoksi. Tyhjä, null, undefined ja
 *  false jäävät pois: poissaolo on oletus, ei arvo. `true` on "1",
 *  kuten API:n `vain_avoimet=1`. */
export function haunSarja(arvot: Record<string, Arvo>): string {
  const p = new URLSearchParams();
  for (const [avain, v] of Object.entries(arvot)) {
    if (v === "" || v === null || v === undefined || v === false) continue;
    p.set(avain, v === true ? "1" : String(v));
  }
  return p.toString();
}

/** Osoitteen kyselyosa jäsennettynä — `useState`-alustajaa varten. */
export function lueHaku(osoiteHaku: string | undefined): URLSearchParams {
  return new URLSearchParams(osoiteHaku ?? "");
}

/** Kokonaislukuparametri tai "" (= ei valittu). */
export function haunLuku(p: URLSearchParams, avain: string): number | "" {
  const n = Number(p.get(avain));
  return Number.isInteger(n) && n > 0 ? n : "";
}

export function useOsoiteHaku(
  osoiteHaku: string | undefined,
  asetaOsoiteHaku: ((haku: string) => void) | undefined,
  arvot: Record<string, Arvo>,
  sovella: (p: URLSearchParams) => void,
): void {
  const sarja = haunSarja(arvot);
  const viimeksi = useRef<string | null>(null);
  const sovellaRef = useRef(sovella);
  sovellaRef.current = sovella;

  // Osoite → näkymä.
  useEffect(() => {
    const h = osoiteHaku ?? "";
    if (viimeksi.current !== null && h === viimeksi.current) return;
    viimeksi.current = h;
    sovellaRef.current(new URLSearchParams(h));
  }, [osoiteHaku]);

  // Näkymä → osoite. Ensimmäinen ajo ohitetaan: mountissa osoite on
  // totuus (ks. otsikkokommentti).
  const ensimmainen = useRef(true);
  useEffect(() => {
    if (ensimmainen.current) { ensimmainen.current = false; return; }
    if (sarja === viimeksi.current) return;
    viimeksi.current = sarja;
    asetaOsoiteHaku?.(sarja);
  }, [sarja, asetaOsoiteHaku]);
}
